import { apiSlice } from './apiSlice';

export const metricsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({

    /**
     * One account's metrics. `category`: simple | advanced. Both carry
     * `flashcard`, `account_information` (the account's record, `challenge`
     * nested), `recent_trades` and `statistics`; advanced adds
     * `volume_by_symbol`, `instrument_breakdown`, `risk_execution`,
     * `session_performance`, `session_summary`, `directional_bias` and
     * `asset_class_mix`.
     */
    getAccountBreakdown: builder.query({
      query: ({ loginId, ...params }) => ({
        url: `/v2/account-metrics/accounts/${encodeURIComponent(loginId)}/`,
        params,
      }),
      providesTags: ['Account'],
    }),

    /**
     * `GET /get/breach/data/<login>/` — the account's breach, as the live
     * dashboard's Breach Notification reads it. `{ detail, data }`: `data` is
     * null for an account with none ("Account is Active."), else one record —
     * `category`, `action`, `created_at`, `admin_comment` and `meta_data`
     * (`reason`, `asset_key`, `platform`, `account_size`, `threshold`,
     * `breach_loss`, `breach_id`, `open_positions`,
     * `contributing_closed_trades`, `calculation_breakdown`, …).
     */
    getBreachData: builder.query({
      query: (loginId) => `/get/breach/data/${encodeURIComponent(loginId)}/`,
      providesTags: (result, error, loginId) => [{ type: 'Account', id: String(loginId) }],
    }),

    /**
     * `GET /v2/inactivity/<login>/?platform=<platform>` — the account's
     * inactivity state. Login ids can carry a "#" ("D#2546707"), hence the
     * encoding.
     */
    getAccountInactivity: builder.query({
      query: ({ loginId, platform }) => ({
        url: `/v2/inactivity/${encodeURIComponent(loginId)}/`,
        params: platform ? { platform } : undefined,
      }),
      providesTags: (result, error, { loginId }) => [{ type: 'Account', id: String(loginId) }],
    }),

    /**
     * `GET /v2/account/<login>/loss-limit-alert/?platform=<platform>` — the
     * warning before a loss limit is reached (review doc, metrics: "Warn the
     * trader before a limit is reached", the 80% daily-loss banner).
     */
    getLossLimitAlert: builder.query({
      query: ({ loginId, platform }) => ({
        url: `/v2/account/${encodeURIComponent(loginId)}/loss-limit-alert/`,
        params: platform ? { platform } : undefined,
      }),
      providesTags: (result, error, { loginId }) => [{ type: 'Account', id: String(loginId) }],
    }),

    /** Trading objectives, one flat record: `max_loss`, `max_loss_target`, `remaining_max_loss`, … */
    getAccountObjectives: builder.query({
      query: ({ loginId, ...params }) => ({
        url: `/v2/account/objectives-flat/${encodeURIComponent(loginId)}/`,
        params: params?.platform ? { platform: params.platform } : undefined,
      }),
      providesTags: (result, error, { loginId }) => [
        { type: 'Account', id: String(loginId) },
        'Account',
      ],
    }),

    /**
     * `GET /v2/account/public/<login>/?platform=mt5` — the read-only snapshot
     * behind `/p/:accountId`. No session: the link goes to anyone, and an
     * account that isn't shared answers 404 `{ detail: "Account not found." }`.
     */
    getPublicAccount: builder.query({
      query: ({ accountId, platform }) => ({
        url: `/v2/account/public/${encodeURIComponent(accountId)}/`,
        params: { platform },
      }),
    }),

    /** The equity curve. `range`: 1d | 1w | 1m | 3m | all. */
    getPerformanceChart: builder.query({
      query: ({ loginId, ...params }) => ({
        url: `/v2/account-metrics/performance-chart/${encodeURIComponent(loginId)}/`,
        params,
      }),
      providesTags: ['Account'],
    }),

    /**
     * Account analysis endpoint from main branch:
     * `GET /v2/account-analysis/?login_id=<login>&platform=<platform>`
     */
    getAccountAnalysis: builder.query({
      query: ({ loginId, platform }) => ({
        url: '/v2/account-analysis/',
        params: {
          login_id: loginId,
          ...(platform ? { platform } : {}),
        },
      }),
      providesTags: ['Account'],
    }),

    /**
     * Account objectives (nested format) from main branch:
     * `GET /v2/account/objectives/<login>/?platform=<platform>`
     */
    getAccountObjectivesNested: builder.query({
      query: ({ loginId, platform }) => ({
        url: `/v2/account/objectives/${encodeURIComponent(loginId)}/`,
        params: platform ? { platform } : undefined,
      }),
      providesTags: ['Account'],
    }),

    /**
     * Account metrics statistics definitions.
     * `GET /v2/account-metrics/statistics/definitions/?category=simple|advanced`
     */
    getStatisticsDefinitions: builder.query({
      query: (params) => {
        const category = typeof params === 'string' ? params : params?.category ?? 'simple';
        return {
          url: '/v2/account-metrics/statistics/definitions/',
          params: { category: (category || 'simple').toLowerCase() },
        };
      },
      providesTags: ['Account'],
    }),
  }),
});

export const {
  useGetAccountBreakdownQuery,
  useGetAccountObjectivesQuery,
  useGetPerformanceChartQuery,
  useGetPublicAccountQuery,
  useGetAccountAnalysisQuery,
  useGetAccountObjectivesNestedQuery,
  useGetBreachDataQuery,
  useGetAccountInactivityQuery,
  useGetLossLimitAlertQuery,
  useGetStatisticsDefinitionsQuery,
} = metricsApi;

/**
 * An account's warnings and hard breaches as the backend counts them, from the
 * breakdown's `risk_status`:
 *
 *   { level: "hard_breach", count: 0, warnings: [],
 *     hard_breach: { count: 1, hard_breaches: ["Max risk per asset"] } }
 *
 * Account Metrics and Breach Analysis both read it here, so the two agree.
 * `null` where a count isn't sent.
 */
export const riskCounts = (riskStatus) => {
  const count = (value) => (value == null || !Number.isFinite(Number(value)) ? null : Number(value));
  return {
    warnings: count(riskStatus?.count),
    hardBreaches: count(riskStatus?.hard_breach?.count),
  };
};
